import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Grow, IconButton, Modal, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { FeatureAnnouncementKey } from "../../constants/featureAnnouncements";
import { markFeatureView } from "../../store/actions/user";
import type { AppDispatch } from "../../store/state/rootStore";
import type { RootState } from "../../store/state/rootReducer";

const ANNOUNCEMENT_OPEN_DELAY_MS = 700;

type FeatureAnnouncementMedia =
  | { type: "image"; src: string; alt: string }
  | { type: "video"; src: string; poster?: string; title: string };

export interface FeatureAnnouncementSlide {
  title: string;
  description?: string;
  media: FeatureAnnouncementMedia;
}

interface FeatureAnnouncementModalProps {
  featureKey: FeatureAnnouncementKey;
  version: string;
  slides: FeatureAnnouncementSlide[];
  hasBeenSeen: boolean;
  onViewed?: () => void;
}

export const FeatureAnnouncementModal = ({
  featureKey,
  version,
  slides,
  hasBeenSeen,
  onViewed,
}: FeatureAnnouncementModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const userId = useSelector((state: RootState) => state.auth.user_id);
  const reopenRequestId = useSelector((state: RootState) => state.featureAnnouncements.reopenRequestId);
  const requestedAnnouncement = useSelector((state: RootState) => state.featureAnnouncements.requestedAnnouncement);
  const [activeStep, setActiveStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const lastHandledReopenRequestId = useRef(reopenRequestId);

  const slide = slides[activeStep];
  const hasNextStep = activeStep < slides.length - 1;
  const stepLabel = `STEP ${String(activeStep + 1).padStart(2, "0")}`;
  const titleId = `${featureKey}-title`;
  const descriptionId = `${featureKey}-description`;

  useEffect(() => {
    setIsOpen(false);
    setActiveStep(0);

    if (!authenticated || !userId || slides.length === 0 || hasBeenSeen) return undefined;

    const openTimeout = window.setTimeout(() => {
      setIsOpen(true);
    }, ANNOUNCEMENT_OPEN_DELAY_MS);

    return () => window.clearTimeout(openTimeout);
  }, [authenticated, hasBeenSeen, slides.length, userId]);

  useEffect(() => {
    if (
      !reopenRequestId ||
      reopenRequestId === lastHandledReopenRequestId.current ||
      !requestedAnnouncement ||
      requestedAnnouncement.featureKey !== featureKey ||
      requestedAnnouncement.version !== version ||
      !authenticated ||
      !userId ||
      slides.length === 0
    ) {
      return;
    }

    lastHandledReopenRequestId.current = reopenRequestId;
    setActiveStep(0);
    setIsOpen(true);
  }, [authenticated, featureKey, reopenRequestId, requestedAnnouncement, slides.length, userId, version]);

  const handleComplete = useCallback(() => {
    if (isSaving) return;

    setIsOpen(false);
    setIsSaving(true);
    void dispatch(markFeatureView(featureKey, version))
      .then((wasMarked) => {
        if (wasMarked) onViewed?.();
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [dispatch, featureKey, isSaving, onViewed, version]);

  const handleDismiss = handleComplete;

  const handleNext = () => {
    if (hasNextStep) {
      setActiveStep((currentStep) => currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    setActiveStep((currentStep) => Math.max(currentStep - 1, 0));
  };

  if (!slide) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleDismiss}
      closeAfterTransition
      BackdropProps={{ timeout: 220 }}
      aria-labelledby={titleId}
      aria-describedby={slide.description ? descriptionId : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Grow in={isOpen} timeout={{ enter: 280, exit: 180 }}>
        <Box
          sx={{
            width: "min(980px, 100%)",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            borderRadius: "4px",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              minHeight: { xs: 0, sm: 420 },
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "42%" },
                p: { xs: 3, sm: "88px 40px 48px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                  color: "#087F6A",
                  fontFamily: "PublicSansFont, Helvetica, Arial, sans-serif",
                  fontSize: "12px",
                  letterSpacing: "4px",
                  fontWeight: 600,
                }}
              >
                {stepLabel}
              </Typography>
              <Typography
                id={titleId}
                sx={{
                  color: "#0D1738",
                  fontFamily: "PublicSansFont, Helvetica, Arial, sans-serif",
                  fontSize: { xs: 30, sm: 38 },
                  lineHeight: 1.15,
                }}
              >
                {slide.title}
              </Typography>
              {slide.description && (
                <Typography
                  id={descriptionId}
                  sx={{
                    mt: 2,
                    color: "#514B49",
                    fontFamily: "PublicSansFont, Helvetica, Arial, sans-serif",
                    fontSize: { xs: 14, sm: 17 },
                    lineHeight: 1.65,
                  }}
                >
                  {slide.description}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "58%" },
                minHeight: { xs: 240, sm: 420 },
                backgroundColor: "#F8F2EA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {slide.media.type === "video" ? (
                <Box
                  component="video"
                  src={slide.media.src}
                  poster={slide.media.poster}
                  controls
                  playsInline
                  aria-label={slide.media.title}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Box
                  component="img"
                  src={slide.media.src}
                  alt={slide.media.alt}
                  sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              )}
            </Box>

            <IconButton
              aria-label="Close feature announcement"
              onClick={handleDismiss}
              disabled={isSaving}
              sx={{ position: "absolute", top: 12, right: 12, color: "#464C53", backgroundColor: "#FFFFFFCC" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              minHeight: 68,
              px: { xs: 2, sm: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid #E1E1E1",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {activeStep > 0 && (
                <IconButton aria-label="Previous step" onClick={handleBack} size="small">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <Box sx={{ display: "flex", gap: 0.5 }} aria-label={`Step ${activeStep + 1} of ${slides.length}`}>
                {slides.map((currentSlide, index) => (
                  <IconButton
                    key={currentSlide.title}
                    aria-label={`Go to step ${index + 1}`}
                    aria-current={index === activeStep ? "step" : undefined}
                    onClick={() => setActiveStep(index)}
                    size="small"
                    sx={{ p: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: index === activeStep ? "#0D1738" : "#D9D9D9",
                      }}
                    />
                  </IconButton>
                ))}
              </Box>
              <Typography sx={{ color: "#857D7A", fontSize: 12, fontWeight: 400, whiteSpace: "nowrap" }}>
                {activeStep + 1} of {slides.length}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSaving}
              sx={{
                minWidth: 88,
                minHeight: 42,
                py: 1,
                px: 2,
                backgroundColor: "#239B8B",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 700,
                "&:hover": { backgroundColor: "#1C8174" },
              }}
            >
              {hasNextStep ? "Next" : "Done"}
            </Button>
          </Box>
        </Box>
      </Grow>
    </Modal>
  );
};
